import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';
import { COMPETENCIES, APPROVAL_STATUSES, MILESTONE_LEVELS } from '../lib/constants';

export default function FacultyDashboard({ isMobile, onEvaluate }) {
  const { user, profile } = useAuth();
  const [entries, setEntries]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [filter, setFilter]       = useState('pending');
  const [decision, setDecision]   = useState('');
  const [comment, setComment]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => { if (profile?.full_name) fetchEntries(); }, [user, profile]);

  const fetchEntries = async () => {
    if (!profile?.full_name) return;
    setLoading(true);
    const { data } = await supabase
      .from('entries').select('*')
      .contains('assigned_faculty_names', [profile.full_name])
      .order('created_at', { ascending: false });
    setEntries(data || []);
    setLoading(false);
  };

  const submitDecision = async () => {
    if (!decision) return;
    if ((decision === 'approved_with_comments' || decision === 'rejected') && !comment.trim()) {
      alert('Please add a comment explaining your decision.'); return;
    }
    setSaving(true);
    await supabase.from('entries').update({
      approval_status: decision,
      faculty_comment: comment,
      reviewed_at: new Date().toISOString(),
    }).eq('id', selected.id);
    setSaving(false);
    setSelected(null); setDecision(''); setComment('');
    fetchEntries();
  };

  const filtered = filter === 'all' ? entries : entries.filter(e => e.approval_status === filter);
  const pendingCount = entries.filter(e => e.approval_status === 'pending').length;
  const comp = (id) => COMPETENCIES.find(c => c.id === id);

  if (loading) return <div style={s.loading}>Loading your cases…</div>;

  // Review panel
  if (selected) {
    const level = MILESTONE_LEVELS.find(l => l.n === selected.milestone_level);
    return (
      <div style={s.content}>
        <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:28}}>
          <button onClick={()=>{setSelected(null);setDecision('');setComment('');}} style={s.backBtn}>← Back</button>
          <h1 style={{...s.title,marginBottom:0}}>Review Case</h1>
        </div>

        <div style={s.reviewGrid}>
          <div style={{display:'flex',flexDirection:'column',gap:18}}>
            <div style={s.card}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}>
                <div style={{fontFamily:"'DM Serif Display'",fontSize:22,color:'#1A1A1F'}}>{selected.date}</div>
                <div style={{display:'flex',gap:8}}>
                  {selected.is_difficult&&<span style={s.difficultBadge}>⚑ Difficult</span>}
                  <span style={s.settingBadge}>{selected.setting}</span>
                </div>
              </div>
              <div style={s.section}><div style={s.sectionLabel}>Fellow</div><div style={s.sectionText}>{selected.fellow_name}</div></div>
              <div style={s.section}><div style={s.sectionLabel}>Case Summary</div><div style={s.sectionText}>{selected.case_summary}</div></div>
              {selected.reflection&&<div style={s.section}><div style={s.sectionLabel}>Fellow's Reflection</div><div style={s.sectionText}>{selected.reflection}</div></div>}
            </div>

            <div style={s.card}>
              <div style={s.sectionLabel}>Competencies Addressed</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:10}}>
                {(selected.competency_ids||[]).map(id=>{const c=comp(id);return c?<span key={id} style={{padding:'5px 12px',borderRadius:20,fontSize:12,fontWeight:500,background:c.color,color:'#fff'}}>{c.label}</span>:null;})}
              </div>
            </div>

            <div style={s.card}>
              <div style={s.sectionLabel}>Self-Assessed Milestone</div>
              <div style={{display:'flex',alignItems:'baseline',gap:8,marginTop:8}}>
                <span style={{fontFamily:"'DM Serif Display'",fontSize:40,color:'#2D6A8A'}}>{selected.milestone_level}</span>
                <div><div style={{fontSize:14,fontWeight:600,color:'#333'}}>{level?.label}</div><div style={{fontSize:12,color:'#888'}}>{level?.desc}</div></div>
              </div>
            </div>
          </div>

          {/* Decision panel */}
          <div style={{display:'flex',flexDirection:'column',gap:18}}>
            {selected.approval_status !== 'pending' ? (
              <div style={s.card}>
                <div style={s.sectionLabel}>Already Reviewed</div>
                <div style={{marginTop:10,fontSize:14,fontWeight:600,color:APPROVAL_STATUSES[selected.approval_status]?.color}}>{APPROVAL_STATUSES[selected.approval_status]?.label}</div>
                {selected.faculty_comment&&<div style={{marginTop:10,fontSize:13,color:'#555',fontStyle:'italic'}}>"{selected.faculty_comment}"</div>}
                <div style={{marginTop:10,fontSize:11,color:'#aaa'}}>Reviewed: {selected.reviewed_at ? new Date(selected.reviewed_at).toLocaleDateString() : '—'}</div>
              </div>
            ) : (
              <div style={s.card}>
                <h3 style={s.cardTitle}>Your Decision</h3>
                <p style={{fontSize:12,color:'#999',marginBottom:16}}>Select an outcome for this case</p>
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {[
                    {val:'approved',            label:'✓ Approved',                        desc:'Case meets competency requirements',       border:'#A8DCC0',bg:'#F0FFF6',active:'#1E5C40'},
                    {val:'approved_with_comments',label:'✓ Approved with Comments',        desc:'Passed but feedback provided below',       border:'#A8C8DC',bg:'#EEF5FB',active:'#2D6A8A'},
                    {val:'rejected',            label:'✗ Rejected — Does Not Meet Level',  desc:'Does not meet competency requirements',    border:'#F4A8A8',bg:'#FFF0F0',active:'#8A2D2D'},
                  ].map(opt=>(
                    <button key={opt.val} onClick={()=>setDecision(opt.val)}
                      style={{padding:'12px 16px',borderRadius:10,border:`1.5px solid ${decision===opt.val?opt.active:opt.border}`,background:decision===opt.val?opt.active:opt.bg,cursor:'pointer',textAlign:'left',fontFamily:"'DM Sans'",transition:'all 0.15s'}}>
                      <div style={{fontWeight:600,fontSize:13,color:decision===opt.val?'#fff':opt.active}}>{opt.label}</div>
                      <div style={{fontSize:11,marginTop:3,color:decision===opt.val?'rgba(255,255,255,0.75)':'#888'}}>{opt.desc}</div>
                    </button>
                  ))}
                </div>

                <div style={{marginTop:18}}>
                  <label style={{display:'block',fontSize:11,fontWeight:600,color:'#555',marginBottom:6,textTransform:'uppercase',letterSpacing:0.4}}>
                    Comment {(decision==='approved_with_comments'||decision==='rejected') ? <span style={{color:'#C42A2A'}}>*</span> : '(optional)'}
                  </label>
                  <textarea value={comment} rows={4} placeholder="Provide feedback for the fellow…"
                    onChange={e=>setComment(e.target.value)}
                    style={{width:'100%',padding:'10px 13px',border:'1.5px solid #E8E4E0',borderRadius:8,fontSize:13,fontFamily:"'DM Sans'",resize:'vertical',outline:'none',boxSizing:'border-box'}} />
                </div>

                <button onClick={submitDecision} disabled={!decision||saving}
                  style={{width:'100%',marginTop:14,padding:'12px',background:decision?'#1A1A1F':'#CCC',color:'#F5F0E8',border:'none',borderRadius:8,fontSize:14,fontWeight:600,cursor:decision?'pointer':'default',fontFamily:"'DM Sans'"}}>
                  {saving ? 'Submitting…' : 'Submit Decision'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.content}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>My Review Queue</h1>
          <p style={s.sub}>Cases assigned to you for review</p>
        </div>
        {pendingCount > 0 && <div style={s.pendingBadge}>{pendingCount} pending review</div>}
          <button onClick={onEvaluate} style={{ padding:'10px 16px', background:'#1A3A5C', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" }}>★ Evaluate Fellow</button>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:20}}>
        {['pending','approved','approved_with_comments','rejected','all'].map(f=>{
          const info = f==='all'?{label:'All',color:'#555',bg:'#EEE'}:APPROVAL_STATUSES[f];
          const isActive = filter===f;
          return <button key={f} onClick={()=>setFilter(f)}
            style={{padding:'6px 14px',borderRadius:20,border:`1px solid ${isActive?(info?.color||'#555'):'#DDD'}`,background:isActive?(info?.color||'#555'):'transparent',color:isActive?'#fff':(info?.color||'#555'),fontSize:12,cursor:'pointer',fontFamily:"'DM Sans'",fontWeight:500}}>
            {info?.label||'All'} ({f==='all'?entries.length:entries.filter(e=>e.approval_status===f).length})
          </button>;
        })}
      </div>

      {filtered.length===0
        ? <div style={s.empty}>No {filter==='all'?'':filter} cases{filter==='pending'?' — all caught up! ✓':''}</div>
        : <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {filtered.map(e=>{
              const status = APPROVAL_STATUSES[e.approval_status];
              return (
                <div key={e.id} onClick={()=>setSelected(e)} style={{...s.entryRow,borderLeft:`3px solid ${status?.color||'#DDD'}`}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:5}}>
                        <span style={{fontSize:12,fontWeight:700,color:'#2D6A8A'}}>{e.fellow_name}</span>
                        <span style={{fontSize:11,color:'#999'}}>{e.date}</span>
                        <span style={{fontSize:11,background:'#F0EDE8',color:'#666',padding:'2px 8px',borderRadius:20}}>{e.setting}</span>
                        {e.is_difficult&&<span style={s.difficultBadge}>⚑</span>}
                      </div>
                      <div style={{fontSize:13,color:'#333',lineHeight:1.5}}>{(e.case_summary||'').slice(0,110)}{(e.case_summary||'').length>110?'…':''}</div>
                      <div style={{display:'flex',gap:5,marginTop:7,flexWrap:'wrap'}}>
                        {(e.competency_ids||[]).map(id=>{const c=comp(id);return c?<span key={id} style={{fontSize:10,padding:'2px 8px',borderRadius:20,background:c.color+'18',color:c.color,fontWeight:500}}>{c.label}</span>:null;})}
                      </div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8,marginLeft:16}}>
                      <span style={{fontSize:11,fontWeight:600,padding:'4px 10px',borderRadius:20,background:status?.bg,color:status?.color,border:`1px solid ${status?.border}`,whiteSpace:'nowrap'}}>{status?.label}</span>
                      <span style={{fontFamily:"'DM Serif Display'",fontSize:22,color:'#2D6A8A'}}>Lvl {e.milestone_level}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

const s = {
  content:{padding:'36px 40px',maxWidth:1100,margin:'0 auto'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24},
  title:{fontFamily:"'DM Serif Display'",fontSize:30,color:'#1A1A1F',marginBottom:4},
  sub:{fontSize:13,color:'#888'},
  loading:{padding:60,textAlign:'center',color:'#888',fontFamily:"'DM Sans'"},
  pendingBadge:{background:'#FFF0E8',color:'#C4622A',border:'1px solid #F4C8A8',padding:'8px 16px',borderRadius:20,fontSize:13,fontWeight:600},
  card:{background:'#fff',borderRadius:12,padding:'22px 24px',boxShadow:'0 2px 8px rgba(0,0,0,0.05)'},
  cardTitle:{fontFamily:"'DM Serif Display'",fontSize:17,color:'#1A1A1F',marginBottom:4},
  reviewGrid:{display:'grid',gridTemplateColumns:'1.3fr 1fr',gap:20,alignItems:'start'},
  section:{marginBottom:18},
  sectionLabel:{fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:'uppercase',color:'#999',marginBottom:6},
  sectionText:{fontSize:14,color:'#333',lineHeight:1.75},
  entryRow:{background:'#fff',borderRadius:10,padding:'14px 18px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',cursor:'pointer',transition:'background 0.15s'},
  settingBadge:{fontSize:11,background:'#F0EDE8',color:'#666',padding:'2px 8px',borderRadius:20},
  difficultBadge:{fontSize:10,background:'#FFF0E8',color:'#C4622A',padding:'2px 8px',borderRadius:20,fontWeight:600,border:'1px solid #F4C8A8'},
  backBtn:{background:'#EEE',color:'#444',border:'none',borderRadius:8,padding:'8px 14px',fontSize:13,cursor:'pointer',fontFamily:"'DM Sans'"},
  empty:{textAlign:'center',padding:'60px 0',color:'#aaa',fontSize:15},
};
